import { z } from "zod";

// Base schema for common fields between create and edit
const baseSubscriptionSchema = z.object({
  siteStructureId: z
    .union([z.string(), z.number()])
    .transform(val => String(val))
    .pipe(z.string().min(1, "Country / Region is required")),

  site: z.enum(["legal500.com", "legal500.de", "legal500.fr", "legal500.cn"], {
    message: "Please select a site",
  }),

  language: z.enum(["en", "de", "fr", "zh"], {
    message: "Please select a language",
  }),

  startDate: z.string().min(1, "Start Date is required"),

  endDate: z.string().min(1, "End Date is required"),

  status: z.enum(["pending", "complete", "cancelled"]),
});

// Metadata schema shared between create and edit
const metadataSchema = z
  .object({
    contact_ids: z.array(z.union([z.number(), z.string()])).optional(),
    editorial_category_ids: z.array(z.union([z.number(), z.string()])).optional(),
    office_ids: z.array(z.number()).optional(),
    office_contact_pairs: z
      .array(
        z
          .object({
            office_id: z.number().nullable(),
            contact_ids: z.array(z.number()),
          })
          .refine(
            pair =>
              !pair.office_id ||
              pair.office_id === 0 ||
              (pair.contact_ids && pair.contact_ids.length > 0),
            {
              message: "Please select at least one lawyer for this office",
              path: ["contact_ids"],
            },
          ),
      )
      .optional(),
  })
  .optional();

// Schema for creating a new subscription
export const createSubscriptionSchema = baseSubscriptionSchema
  .extend({
    firmId: z.coerce.number().int().positive("Company is required"),

    packageId: z
      .string()
      .min(1, "Package is required")
      .refine(val => val !== "", { message: "Please select a package" }),

    billingTaxonomyTermId: z
      .union([z.string(), z.number()])
      .transform(val => String(val))
      .pipe(z.string().min(1, "Region is required")),

    correlationId: z.string().optional().or(z.literal("")),

    companySlug: z.string().optional().or(z.literal("")),

    crmAccountId: z.string().optional().or(z.literal("")),

    metadata: metadataSchema,
  })
  .refine(
    data => {
      if (!data.startDate || !data.endDate) return true;
      return new Date(data.endDate) > new Date(data.startDate);
    },
    {
      message: "End Date must be after Start Date",
      path: ["endDate"],
    },
  );

// Schema for editing an existing subscription
export const editSubscriptionSchema = baseSubscriptionSchema
  .extend({
    firmId: z.coerce.number().int().positive("Company is required"),

    packageId: z
      .string()
      .min(1, "Package is required")
      .refine(val => val !== "", { message: "Please select a package" }),

    companySlug: z.string().optional().or(z.literal("")),

    crmAccountId: z.string().optional().or(z.literal("")),

    metadata: metadataSchema,
  })
  .refine(
    data => {
      if (!data.startDate || !data.endDate) return true;
      return new Date(data.endDate) > new Date(data.startDate);
    },
    {
      message: "End Date must be after Start Date",
      path: ["endDate"],
    },
  );

// Type exports
export type CreateSubscriptionInput = z.infer<typeof createSubscriptionSchema>;
export type EditSubscriptionInput = z.infer<typeof editSubscriptionSchema>;
